import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { classes } from '../styles';
import InfoBox from '../../../Components/CustomUIElements/InfoBox';
import NoAutoStatusSVG from '../AutoStatuses/NoAutoStatusSVG';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import { EqualsField, IfField, ThenField } from './fieldOptions';
import {
  deepCopy,
  useAddAutoTagCondition,
  useAutoTagConditions,
  useCreateTag,
  useDeleteAutoTagCondition,
  useSupportedQuestions,
  useTags,
  useUpdateAutoTagCondition
} from './data';
import { AutoTaggingProps, IOptionType, TransformedAutoTagCondition } from './type';

/**
 * Component Structure:
 * - AutoTagging: Main component that orchestrates the auto tagging functionality
 * - IfField: Component for question selection (extracted to separate file)
 * - EqualsField: Component for answer selection (extracted to separate file)
 * - ThenField: Component for tag selection (extracted to separate file)
 *
 * Data fetching and mutations are extracted to data.tsx
 */

export default function AutoTagging({ jobId, setSnackbar, handleEditTabChange }: AutoTaggingProps) {
  const [hoveredField, setHoveredField] = useState<[number, number]>([-1, -1]);
  const [autoTagConditions, setAutoTagConditions] = useState<TransformedAutoTagCondition[]>([]);
  const [isCreatingTag, setIsCreatingTag] = useState<boolean>(false);

  // Use the extracted hooks for data fetching
  const { data: supportedQuestions, isLoading: supportedQuestionIsLoading } =
    useSupportedQuestions(jobId);
  const { data: tags = [] } = useTags();
  const { fetchAutoTagConditions } = useAutoTagConditions(jobId, setAutoTagConditions, setSnackbar);

  // Use the createTag mutation hook
  const createTagMutation = useCreateTag(setSnackbar);

  useEffect(() => {
    fetchAutoTagConditions();
  }, [fetchAutoTagConditions]);
  // Use the extracted mutation hooks
  const addAutoTagConditionMutation = useAddAutoTagCondition(
    jobId,
    supportedQuestions || [],
    tags,
    autoTagConditions,
    setAutoTagConditions,
    setSnackbar
  );

  const deleteAutoTagConditionMutation = useDeleteAutoTagCondition(
    jobId,
    autoTagConditions,
    setAutoTagConditions,
    setSnackbar
  );

  const updateAutoTagConditionMutation = useUpdateAutoTagCondition(
    jobId,
    autoTagConditions,
    setAutoTagConditions,
    setSnackbar
  );

  // Handler functions that use the mutations
  const handleAddNewAutoTagCondition = () => addAutoTagConditionMutation.mutate();
  const handleDeleteNewAutoTagCondition = (index: number) => {
    deleteAutoTagConditionMutation.mutate({ index, id: autoTagConditions[index].id });
  };

  const handleRemoveField = (index: number, fieldIndex: number) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].fields.splice(fieldIndex, 1);

    // If no fields left, don't proceed
    if (tempAutoTagCondition[index].fields.length === 0) {
      return;
    }

    const previousAutoTagConditions = deepCopy(autoTagConditions);
    setAutoTagConditions(tempAutoTagCondition);

    // Get the first field's question and choices
    const firstField = tempAutoTagCondition[index].fields[0];
    const fieldChoiceIds = firstField.choices.map((choice) => choice.id);

    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: firstField.question.id,
          field_choice_ids: fieldChoiceIds,
          tag_ids: tempAutoTagCondition[index].tags.map((tag) => tag.id),
          updateIndex: index
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleChangeField = (
    index: number,
    fieldIndex: number,
    value: number | number[],
    type: 'q' | 'a'
  ) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);

    if (type === 'q') {
      // When changing the question
      const questionId = Array.isArray(value) ? value[0] : value;
      const selectedQuestion = supportedQuestions.find((question) => question.id === questionId);

      tempAutoTagCondition[index].fields[fieldIndex].question.id = questionId;

      // For CheckBoxField, we can select multiple choices
      const isCheckbox = selectedQuestion?.type === 'CheckBoxField';

      // Update choices based on question type
      if (isCheckbox) {
        // For CheckBoxField, use all choices initially
        tempAutoTagCondition[index].fields[fieldIndex].choices =
          selectedQuestion?.field_choices || [];
      } else {
        // For other types, use just the first choice
        tempAutoTagCondition[index].fields[fieldIndex].choices = selectedQuestion?.field_choices
          ? [selectedQuestion.field_choices[0]]
          : [];
      }
    } else {
      // When changing the answer(s)
      const selectedQuestion = supportedQuestions.find(
        (question) => question.id === tempAutoTagCondition[index].fields[fieldIndex].question.id
      );

      // For CheckBoxField, we can select multiple choices
      const isCheckbox = selectedQuestion?.type === 'CheckBoxField';

      if (isCheckbox && Array.isArray(value)) {
        // For CheckBoxField, update multiple choices
        const selectedChoices =
          selectedQuestion?.field_choices.filter((choice) => value.includes(choice.id)) || [];
        tempAutoTagCondition[index].fields[fieldIndex].choices = selectedChoices;
      } else {
        // For other types, update single choice
        const choiceId = Array.isArray(value) ? value[0] : value;
        const selectedChoice = selectedQuestion?.field_choices.find(
          (choice) => choice.id === choiceId
        );
        tempAutoTagCondition[index].fields[fieldIndex].choices = selectedChoice
          ? [selectedChoice]
          : [];
      }
    }

    setAutoTagConditions(tempAutoTagCondition);

    // Get field choice IDs from the updated field
    const fieldChoiceIds = tempAutoTagCondition[index].fields[fieldIndex].choices.map(
      (choice) => choice.id
    );

    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: tempAutoTagCondition[index].fields[fieldIndex].question.id,
          field_choice_ids: fieldChoiceIds,
          tag_ids: tempAutoTagCondition[index].tags.map((tag) => tag.id)
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleChangeTags = (index: number, selectedTags: IOptionType[]) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].tags = selectedTags;
    setAutoTagConditions(tempAutoTagCondition);

    // Get the first field's question and choices
    const firstField = tempAutoTagCondition[index].fields[0];
    const fieldChoiceIds = firstField.choices.map((choice) => choice.id);

    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: firstField.question.id,
          field_choice_ids: fieldChoiceIds,
          tag_ids: selectedTags.map((tag) => tag.id)
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  // Handler for creating a new tag
  const handleCreateTag = (tagName: string, statusIndex?: number) => {
    setIsCreatingTag(true);
    createTagMutation.mutate(tagName, {
      onSuccess: (data) => {
        setIsCreatingTag(false);
        // If statusIndex is provided, add the new tag to the selected tags for that condition
        if (statusIndex !== undefined && data) {
          const tempAutoTagCondition = deepCopy(autoTagConditions);

          const newTag = {
            id: data.id,
            name: data.name || tagName
          };

          // Add the new tag to the selected tags if it doesn't already exist
          if (!tempAutoTagCondition[statusIndex].tags.some((tag) => tag.id === newTag.id)) {
            tempAutoTagCondition[statusIndex].tags.push(newTag);
            setAutoTagConditions(tempAutoTagCondition);

            // Update the backend with the new tag
            const firstField = tempAutoTagCondition[statusIndex].fields[0];
            const fieldChoiceIds = firstField.choices.map((choice) => choice.id);
            const tagIds = tempAutoTagCondition[statusIndex].tags.map((tag) => tag.id);

            updateAutoTagConditionMutation.mutate({
              conditionId: autoTagConditions[statusIndex].id,
              data: {
                field_id: firstField.question.id,
                field_choice_ids: fieldChoiceIds,
                tag_ids: tagIds
              }
            });
          }
        }
      },
      onError: () => {
        setIsCreatingTag(false);
      }
    });
  };

  const handleEnableCondition = (index: number, conditionId: number) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].enabled = !autoTagConditions[index].enabled;
    setAutoTagConditions(tempAutoTagCondition);

    // For enabling/disabling, we only need to update the enabled status
    // No need to send field_choice_ids as they're not changing
    updateAutoTagConditionMutation.mutate(
      { conditionId, data: { enabled: tempAutoTagCondition[index].enabled } },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  return (
    <Box sx={classes.automationContainer}>
      <h2 data-testid="auto-auto tags-header">Auto Tags</h2>
      <InfoBox message="Any changes here will be automatically saved and will be applied immediately." />
      <Box sx={classes.toggleButtonsContainer}>
        <ToggleButtonGroup value={'questionBased'} exclusive aria-label="question or rating based">
          <ToggleButton
            data-testid="question-based-toggle-button"
            value="questionBased"
            sx={sharedClasses.toggleButton}
          >
            Question Based
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={classes.autoStatusesContainer}>
        <Box sx={{ position: 'relative' }}>
          {supportedQuestionIsLoading ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={450}
              width={'100%'}
              sx={{ borderRadius: '8px' }}
            />
          ) : supportedQuestions && supportedQuestions.length > 0 ? (
            <>
              {autoTagConditions &&
                autoTagConditions?.map((autoTagCondition, statusIndex) => (
                  <Box sx={{ position: 'relative', display: 'flex' }} key={autoTagCondition.id}>
                    <Box sx={classes.autoStatusesSubContainer} key={autoTagCondition.id}>
                      {autoTagCondition.fields?.map((field, fieldIndex) => (
                        <Box
                          sx={classes.statusFormLine}
                          key={field.id}
                          onMouseEnter={() => setHoveredField([statusIndex, fieldIndex])}
                          onMouseLeave={() => setHoveredField([-1, -1])}
                        >
                          <IfField
                            field={field}
                            fieldIndex={fieldIndex}
                            statusIndex={statusIndex}
                            supportedQuestions={supportedQuestions}
                            autoTagCondition={autoTagCondition}
                            handleChangeField={handleChangeField}
                          />
                          <EqualsField
                            field={field}
                            fieldIndex={fieldIndex}
                            statusIndex={statusIndex}
                            supportedQuestions={supportedQuestions}
                            autoTagConditions={autoTagConditions}
                            handleChangeField={handleChangeField}
                            updateAutoTagConditionMutation={updateAutoTagConditionMutation}
                          />
                          {hoveredField[1] > 0 &&
                            hoveredField.join('') === `${statusIndex}${fieldIndex}` && (
                              <IconButton
                                onClick={() => handleRemoveField(statusIndex, fieldIndex)}
                                sx={classes.clearButton}
                              >
                                {updateAutoTagConditionMutation.isPending ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <ClearIcon />
                                )}
                              </IconButton>
                            )}
                        </Box>
                      ))}
                      <Box sx={classes.statusFormLine}>
                        <ThenField
                          statusIndex={statusIndex}
                          autoTagCondition={autoTagCondition}
                          tags={tags}
                          handleChangeTags={handleChangeTags}
                          isCreatingTag={isCreatingTag}
                          handleCreateTag={handleCreateTag}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        ...classes.switchContainer,
                        fontWeight: autoTagCondition.enabled ? 'bold' : ''
                      }}
                    >
                      <Box sx={{ marginTop: '12px' }}>
                        <Switch
                          sx={autoTagCondition.enabled ? classes.switchActive : classes.switch}
                          checked={autoTagCondition.enabled}
                          size="small"
                          onChange={() => handleEnableCondition(statusIndex, autoTagCondition.id)}
                        />
                        <Stack sx={{ marginTop: 1, fontSize: '12px' }}>Enabled</Stack>
                      </Box>
                    </Box>
                    <IconButton
                      data-testid="delete-auto-status"
                      sx={classes.deleteButton}
                      onClick={() => handleDeleteNewAutoTagCondition(statusIndex)}
                      disabled={deleteAutoTagConditionMutation.isPending}
                    >
                      {deleteAutoTagConditionMutation.isPending ? (
                        <CircularProgress size={26} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Box>
                ))}
              {autoTagConditions && autoTagConditions.length > 0 ? (
                <IconButton
                  sx={classes.addNewStatusButton}
                  onClick={handleAddNewAutoTagCondition}
                  disabled={addAutoTagConditionMutation.isPending}
                >
                  {addAutoTagConditionMutation.isPending ? (
                    <CircularProgress size={21} />
                  ) : (
                    <AddCircleIcon />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ ...classes.noQuestionsContainer, margin: '100px 0px' }}>
                  <Box>
                    <NoAutoStatusSVG />
                  </Box>
                  <Box sx={classes.noQuestionsText}>You have no auto tagging conditions.</Box>
                  <Button
                    data-testid="add-new-status-button"
                    startIcon={
                      addAutoTagConditionMutation.isPending ? (
                        <CircularProgress size={26} />
                      ) : (
                        <AddCircleIcon />
                      )
                    }
                    onClick={handleAddNewAutoTagCondition}
                    sx={classes.noStatusesAddButton}
                  >
                    Add new Tagging Condition
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ ...classes.noQuestionsContainer, margin: '100px 0px' }}>
              <Box>
                <NoAutoStatusSVG />
              </Box>
              <Box sx={classes.noQuestionsText}>
                Auto-tags can only be used with the following question types:
                <br />
                Radio buttons, Checkboxes, and Select dropdowns.
                <br />
                You currently have no questions of these types.
              </Box>
              <Button
                data-testid="create-radio-button"
                startIcon={
                  addAutoTagConditionMutation.isPending ? (
                    <CircularProgress size={26} />
                  ) : (
                    <AddCircleIcon />
                  )
                }
                onClick={(e) => handleEditTabChange && handleEditTabChange(e, 4)}
                sx={classes.noStatusesAddButton}
              >
                Create a supported question type
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
